import { DAEMON_URL } from "../config";
import { sharedRunPath } from "./qaShare";
import { HttpError } from "./errors";

// The qa-agent orchestrator (runners, runner groups, repos, test runs), through
// the daemon. Admin calls go through /admin/qa-orchestrator/api/<path>, which
// the daemon forwards with the orchestrator's token; project calls through
// /projects/:project/qa-test-runs, which the daemon pins to the project's repo.

export type ComputerState = "idle" | "running" | "debugging" | "paused" | "waiting_for_idle" | "unavailable" | "unknown" | "offline" | "disabled" | string;

/** Something a computer needs from a person: "action" when someone has to do
 *  something at it, "info" when it is just busy with a person. */
export interface QaNeedsYou {
  level: "action" | "info";
  id: string;
  message: string;
  hint: string | null;
}

export interface QaComputer {
  id: string;
  name: string;
  baseUrl: string;
  groups: string[];
  enabled: boolean;
  notes: string | null;
  state: ComputerState;
  queued: number | null;
  running: number | null;
  /** The runner's own pause (someone at the machine, or an admin); null when unknown. */
  paused?: boolean | null;
  pauseReason: string | null;
  pausedSince?: string | null;
  lastSeenAt: string | null;
  lastError: string | null;
  activeRunId: string | null;
  /** Why it cannot use its screen right now (state "unavailable"); absent from an older orchestrator. */
  unavailableReason?: string | null;
  needsYou?: QaNeedsYou[];
}

export interface QaRunnerGroup {
  id: string;
  name: string;
  computers: string[];
  notes: string | null;
}

/** Where a run may go: a runner group and/or named runners. Empty: any runner. */
export interface QaTarget {
  computers?: string[];
  group?: string;
}

/** How a repository's runs are started on the runner (the orchestrator's RunOptions). */
export interface QaRunOptions {
  port?: number;
  setupChecks?: Record<string, unknown>[];
  maxRetries?: number;
  recordStartup?: boolean;
}

export interface QaGitHubRepo {
  id: string;
  scope: string;
  enabled: boolean;
  target: QaTarget;
  options: QaRunOptions;
  /** The plans to run; null: every plan in qa/plans/. */
  plans: string[] | null;
  report: "status" | "check";
  context: string;
  comment: boolean;
  allowForks: boolean;
  notes: string | null;
  detailsUrl: string | null;
}

/** Every field of the orchestrator's CreateGitHubRepo; a PATCH takes any of them. */
export interface QaGitHubRepoInput {
  scope: string;
  target: QaTarget;
  options: QaRunOptions;
  plans: string[] | null;
  report: "status" | "check";
  context: string;
  comment: boolean;
  allowForks: boolean;
  notes: string | null;
  detailsUrl: string | null;
}

/** How long finished runs' files are kept on the orchestrator, in days (0-3650). */
export interface QaRetention {
  videoPassDays: number;
  videoFailDays: number;
  screenshotPassDays: number;
  screenshotFailDays: number;
}

export interface QaPullRequest {
  number: number;
  url: string;
  repo?: string;
  base?: string;
  branch?: string;
}

export interface QaJobSummary {
  id: string;
  kind: string;
  status: QaRunStatus;
  plan: string | null;
  testRunId: string | null;
  createdAt: string;
  runs: QaJobRun[];
}

export interface QaApiKey {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export type QaRunStatus = "pending" | "running" | "pass" | "captured" | "fail" | "error" | "cancelled";

/** What started a test run. */
export interface QaTrigger {
  event: string;
  prNumber?: number;
  prTitle?: string;
  url?: string;
  runUrl?: string;
  actor?: string;
}

export interface QaTestRunSummary {
  id: string;
  kind: "test" | "capture";
  repo: string;
  ref: string;
  sha: string;
  target: QaTarget;
  plans: string[];
  packs: string[];
  trigger: QaTrigger | null;
  status: QaRunStatus;
  counts: Record<string, number>;
  createdAt: string;
  /** When its last plan ended; null while one is still open (absent from older orchestrators). */
  finishedAt?: string | null;
}

export interface QaTestOutcome {
  testId: string;
  outcome: { kind: string; reason?: string };
}

export interface QaJobRun {
  id: string;
  status: string;
  computerId: string | null;
  computerName: string | null;
  error: string | null;
  createdAt: string;
  dispatchedAt: string | null;
  finishedAt: string | null;
  result?: { results?: QaTestOutcome[]; error?: string; errorKind?: string } | null;
}

export interface QaJob {
  id: string;
  status: QaRunStatus;
  plan: string | null;
  runs: QaJobRun[];
}

export interface QaTestRun extends QaTestRunSummary {
  jobs: QaJob[];
}

export interface QaOrchestratorStatus {
  configured: boolean;
  url?: string;
  publicUrl?: string;
  reachable?: boolean;
  error?: string;
  syncProjects?: boolean;
  projects?: { id: string; name: string; kind: string; repo: string | null }[];
}

export interface QaSyncResult {
  project: string;
  repo: string | null;
  status: "created" | "exists" | "no_repo" | "error";
  error?: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}${path}`, {
      ...init,
      credentials: "include",
      headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!res.ok) throw new HttpError(res.status, (data as { error?: string })?.error ?? `http ${res.status}`);
  return data as T;
}

/** A call to the orchestrator itself, e.g. orch("GET", "/computers"). Admins only. */
export function orch<T>(method: string, path: string, body?: unknown): Promise<T> {
  return call<T>(`/admin/qa-orchestrator/api${path}`, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
}

export const getOrchestratorStatus = () => call<QaOrchestratorStatus>("/admin/qa-orchestrator");

export type QaConnectionSource = "db" | "env" | "none";

/** Where the daemon reaches the orchestrator. The token itself never comes back. */
export interface QaConnection {
  configured: boolean;
  url: string | null;
  /** Null: the orchestrator URL is used. */
  publicUrl: string | null;
  tokenSet: boolean;
  source: { url: QaConnectionSource; publicUrl: QaConnectionSource; token: QaConnectionSource };
  updatedAt: number | null;
  updatedBy: string | null;
  /** Whether this workspace's GitHub App mints a token, which the orchestrator reads private repos with. */
  github?: QaGithubAppStatus;
}

export type QaGithubAppStatus = { app: "ok" | "not_configured" | "failed"; error?: string };

export const getConnection = () => call<QaConnection>("/admin/qa-orchestrator/connection");
/** An omitted field is unchanged; null (or "") clears what is saved, and the server env applies again. */
export const saveConnection = (patch: { url?: string | null; publicUrl?: string | null; token?: string | null }) =>
  call<QaConnection>("/admin/qa-orchestrator/connection", { method: "PUT", body: JSON.stringify(patch) });
/** Tries the given unsaved values, or the current ones, with an authenticated read. */
export const testConnection = (values: { url?: string; token?: string } = {}) =>
  call<{ ok: boolean; status: number; error?: string }>("/admin/qa-orchestrator/connection/test", { method: "POST", body: JSON.stringify(values) });
export const syncProjects = async () => (await call<{ results: QaSyncResult[] }>("/admin/qa-orchestrator/sync-projects", { method: "POST" })).results;

export const listComputers = async () => (await orch<{ computers: QaComputer[] }>("GET", "/computers")).computers;
export const createComputer = (input: { name: string; baseUrl: string; token: string; enabled?: boolean; notes?: string | null }) =>
  orch<{ computer: QaComputer }>("POST", "/computers", input);
export const updateComputer = (id: string, patch: Partial<{ name: string; baseUrl: string; token: string; enabled: boolean; notes: string | null }>) =>
  orch<{ computer: QaComputer }>("PATCH", `/computers/${encodeURIComponent(id)}`, patch);
export const deleteComputer = (id: string) => orch("DELETE", `/computers/${encodeURIComponent(id)}`);
/** The runner's own emergency stop: ends what it is running and holds its queue until resumed. */
export const pauseRunner = (id: string) => orch("POST", `/computers/${encodeURIComponent(id)}/runner/pause`, { reason: "paused_by_admin" });
export const resumeRunner = (id: string) => orch("POST", `/computers/${encodeURIComponent(id)}/runner/resume`, {});

export const listGroups = async () => (await orch<{ runnerGroups: QaRunnerGroup[] }>("GET", "/runner-groups")).runnerGroups;
export const createGroup = (input: { id: string; name?: string; computers: string[]; notes?: string | null }) => orch("POST", "/runner-groups", input);
export const updateGroup = (id: string, patch: { name?: string; computers?: string[]; notes?: string | null }) =>
  orch("PATCH", `/runner-groups/${encodeURIComponent(id)}`, patch);
export const deleteGroup = (id: string) => orch("DELETE", `/runner-groups/${encodeURIComponent(id)}`);

export const listRepos = async () => (await orch<{ githubRepos: QaGitHubRepo[] }>("GET", "/github-repos")).githubRepos;
export const createRepo = (input: Pick<QaGitHubRepoInput, "scope"> & Partial<QaGitHubRepoInput>) => orch("POST", "/github-repos", input);
export const updateRepo = (id: string, patch: Partial<QaGitHubRepoInput>) => orch("PATCH", `/github-repos/${encodeURIComponent(id)}`, patch);
export const deleteRepo = (id: string) => orch("DELETE", `/github-repos/${encodeURIComponent(id)}`);

export const listApiKeys = async () => (await orch<{ apiKeys: QaApiKey[] }>("GET", "/api-keys")).apiKeys;
/** The key itself is in the answer, and only there. */
export const createApiKey = async (name: string) => (await orch<{ apiKey: QaApiKey & { key: string } }>("POST", "/api-keys", { name })).apiKey;
export const deleteApiKey = (id: string) => orch("DELETE", `/api-keys/${encodeURIComponent(id)}`);

/** The setting and the orchestrator's defaults (absent from an orchestrator older than them). */
export const getRetention = async () => await orch<{ retention: QaRetention; defaults?: QaRetention }>("GET", "/settings/retention");
export const setRetention = async (patch: Partial<QaRetention>) => (await orch<{ retention: QaRetention }>("PUT", "/settings/retention", patch)).retention;

/** Discord notifications: the webhook URL is write-only, only whether one is set comes back. */
export interface QaNotifications {
  discord: { configured: boolean; mention: string | null };
}
export const getNotifications = async () => (await orch<QaNotifications>("GET", "/settings/notifications")).discord;
export const setNotifications = async (patch: { discordWebhookUrl?: string | null; mention?: string | null }) =>
  (await orch<QaNotifications>("PUT", "/settings/notifications", patch)).discord;
export const testNotifications = () => orch("POST", "/settings/notifications/test", {});

// ---- re-baselines: capture runs, per-check review, a PR with the accepted baselines ----


/** An orchestrator image (a re-baseline check's path) as an object URL. */
export async function orchImageUrl(path: string): Promise<string> {
  const res = await fetch(`${DAEMON_URL}/admin/qa-orchestrator/api${path}`, { credentials: "include" });
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

// ---- activity across every repository ----

export const listAllTestRuns = async (limit = 30) => (await orch<{ testRuns: QaTestRunSummary[] }>("GET", `/test-runs?limit=${limit}`)).testRuns;
export const cancelTestRun = (id: string) => orch("DELETE", `/test-runs/${encodeURIComponent(id)}`);
/** Work not finished yet: waiting in the orchestrator's queue or on a runner. */
export const listOpenJobs = async () => (await orch<{ jobs: QaJobSummary[] }>("GET", "/jobs?status=pending,running&limit=100")).jobs;
export const cancelJob = (id: string) => orch("DELETE", `/jobs/${encodeURIComponent(id)}`);

/** The copy-paste GitHub workflow, as the orchestrator serves it. */
export async function getWorkflowYaml(): Promise<string> {
  const res = await fetch(`${DAEMON_URL}/admin/qa-orchestrator/api/ci/qa-tests.yml`, { credentials: "include" });
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return res.text();
}

// ---- a project's test runs ----

const projectPath = (project: string) => `/projects/${encodeURIComponent(project)}/qa-test-runs`;

/** Where a test run's calls go: the project's routes, or the public ones for a shared run (qaShare.ts). */
const testRunPath = (project: string, testRun: string, rest = "") =>
  sharedRunPath(testRun, rest) ?? `${projectPath(project)}/${encodeURIComponent(testRun)}${rest}`;

export const listProjectTestRuns = (project: string) =>
  call<{ configured: boolean; repo: string; testRuns: QaTestRunSummary[]; error?: string }>(projectPath(project));
export const getProjectTestRun = async (project: string, id: string) =>
  (await call<{ testRun: QaTestRun }>(testRunPath(project, id))).testRun;
export const startProjectTestRun = (project: string, ref?: string) =>
  call<{ testRun: QaTestRunSummary }>(projectPath(project), { method: "POST", body: JSON.stringify(ref ? { ref } : {}) });
export const cancelProjectTestRun = (project: string, id: string) =>
  call(`${projectPath(project)}/${encodeURIComponent(id)}`, { method: "DELETE" });

/** A run's screenshot names. */
export async function listRunArtifacts(project: string, testRun: string, job: string, run: string): Promise<string[]> {
  const body = await call<{ artifacts?: (string | { name: string })[] }>(testRunPath(project, testRun, `/jobs/${encodeURIComponent(job)}/runs/${encodeURIComponent(run)}/artifacts`));
  return (body.artifacts ?? []).map((a) => (typeof a === "string" ? a : a.name));
}

/** One plan run on one computer: its files on the daemon. */
export interface QaRunRef {
  project: string;
  testRun: string;
  job: string;
  run: string;
}

const runPath = (r: QaRunRef, path: string) =>
  testRunPath(r.project, r.testRun, `/jobs/${encodeURIComponent(r.job)}/runs/${encodeURIComponent(r.run)}${path}`);

async function runFile(r: QaRunRef, path: string): Promise<Response> {
  const res = await fetch(`${DAEMON_URL}${runPath(r, path)}`, { credentials: "include" });
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return res;
}

/** One screenshot as an object URL (fetched with the session, so <img> needs no cookie of its own). */
export async function runArtifactUrl(project: string, testRun: string, job: string, run: string, name: string): Promise<string> {
  return URL.createObjectURL(await (await runFile({ project, testRun, job, run }, `/artifacts/${encodeURIComponent(name)}`)).blob());
}

/** The run's recording (mp4) as an object URL. */
export const runRecordingUrl = async (r: QaRunRef) => URL.createObjectURL(await (await runFile(r, "/recording")).blob());

export interface QaRecordedStep {
  index: number;
  count: number;
  action: string;
  attempt: number;
  /** Seconds into the test's video. */
  offset: number;
  state?: string;
}

export interface QaRecordedTest {
  testId: string;
  status: string | null;
  file: string;
  duration: number;
  steps: QaRecordedStep[];
}

/** The run's videos, one per test; null for a run recorded as one video (or not at all). */
export const runRecordings = async (r: QaRunRef): Promise<QaRecordedTest[] | null> => {
  try {
    return ((await (await runFile(r, "/recordings")).json()) as { tests?: QaRecordedTest[] }).tests ?? null;
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) return null;
    throw e;
  }
};

/** One test's video as an object URL. */
export const runTestVideoUrl = async (r: QaRunRef, file: string) => URL.createObjectURL(await (await runFile(r, `/recordings/${encodeURIComponent(file)}`)).blob());

/** The live view's latest frame as an object URL. */
export const runScreenUrl = async (r: QaRunRef) => URL.createObjectURL(await (await runFile(r, "/screen/latest")).blob());

export type QaLogSource = "runner" | "server" | "client";

/** A run's log; with `since` (a byte offset) only what was written after it. */
export const runLog = async (r: QaRunRef, source: QaLogSource, since?: number) =>
  (await runFile(r, `/logs/${source}${since ? `?since=${since}` : ""}`)).text();

/** One screenshot check's verdict and images (artifact names), as the runner reported it. */
export interface QaCheck {
  testId: string;
  stepIndex: number;
  checkId: string;
  kind: string;
  reason?: string;
  /** The capture (cropped to the check's region). */
  artifact?: string;
  baseline?: string;
  diff?: string;
  diffRatio?: number;
  tolerance?: number;
  at: string;
}

export interface QaRunProgress {
  status: string;
  phase: string | null;
  label?: string | null;
  progress: {
    phaseDetail?: string;
    activity?: string;
    checks?: QaCheck[];
    test?: { id: string; index: number; count: number; attempt: number };
    step?: { index: number; count: number; action: string; label: string; state: string };
  } | null;
}

export const runProgress = async (r: QaRunRef) => (await (await runFile(r, "/progress")).json()) as QaRunProgress;

// ---- a project's test catalog: every test in its repo at a ref ----

/** A screenshot check as the plan names it: a baseline/mask under qa/baselines, or a text (OCR) check. */
export interface QaCatalogCheck {
  id: string;
  type: "image" | "text";
  baseline?: string;
  mask?: string;
  region?: { x: number | string; y: number | string; width: number | string; height: number | string };
  tolerance?: number;
  contains?: string;
  matches?: string;
}

export interface QaCatalogStep {
  action: string;
  /** e.g. "click (1288, 484)", "command: give @p diamond". */
  summary: string;
  checks?: QaCatalogCheck[];
}

export interface QaCatalogTest {
  id: string;
  description: string;
  expected: string;
  failureLooksLike: string;
  setup: { spawn?: { x: number; y: number; z: number }; gamemode?: string } | null;
  stepCount: number;
  screenshotChecks: number;
  steps: QaCatalogStep[];
  images: { name: string; kind: "baseline" | "mask"; exists: boolean }[];
}

export interface QaCatalogPlan {
  name: string;
  file: string;
  pack: string | null;
  tests: QaCatalogTest[];
  /** Why the plan can't run as committed. */
  error?: string;
  issues?: { path: string; message: string }[];
}

export interface QaCatalog {
  repo: string;
  ref: string;
  sha: string;
  plans: QaCatalogPlan[];
  counts: { plans: number; tests: number; screenshotChecks: number; missingImages: number; brokenPlans: number };
}

const catalogPath = (project: string) => `/projects/${encodeURIComponent(project)}/qa-catalog`;

export const getProjectCatalog = (project: string, ref?: string) =>
  call<{ configured: boolean; repo: string; catalog: QaCatalog | null }>(`${catalogPath(project)}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`);

/** The catalog of uploaded plan files: no repo, ref or sha. */
export interface QaCatalogPreview extends Omit<QaCatalog, "repo" | "ref" | "sha"> {
  source: "upload";
}

/** The catalog of plan files from a local folder (`qa/plans/<name>.plan.yaml` → YAML) and its `qa/baselines/` image paths; nothing is stored. */
export const previewProjectCatalog = (project: string, files: Record<string, string>, images: string[]) =>
  call<{ configured: boolean; catalog: QaCatalogPreview }>(`${catalogPath(project)}/preview`, { method: "POST", body: JSON.stringify({ files, images }) });

/** A baseline at the catalog's commit as an object URL (fetched with the session). */
export async function catalogBaselineUrl(project: string, sha: string, name: string): Promise<string> {
  const path = name.split("/").map(encodeURIComponent).join("/");
  const res = await fetch(`${DAEMON_URL}${catalogPath(project)}/baselines/${path}?sha=${sha}`, { credentials: "include" });
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return URL.createObjectURL(await res.blob());
}
